import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, getJson } from './client'

/**
 * Asset register + lifecycle + site-schematic client (backend app/api/v1/assets.py
 * and schematics.py). Feeds the Assets pages and the 3D twin: the register is the
 * geometry source, the schematic is the calibrated underlay.
 */
const BASE = import.meta.env.VITE_API_BASE_URL || '/api/v1'

export type LifecycleState =
  | 'planned' | 'commissioning' | 'operational' | 'impaired' | 'decommissioned'

export type Asset = {
  asset_id: string
  site_id: string
  name?: string | null
  asset_type?: string | null
  lifecycle_state: LifecycleState
  manufacturer?: string | null
  capacity?: string | null
  commissioned_date?: string | null
  location?: string | null
  pos_x?: number | null
  pos_y?: number | null
  floor?: number | null
  rotation?: number | null
  width?: number | null
  depth?: number | null
  height?: number | null
  telemetry_key?: string | null
}

export type AssetCreate = {
  asset_id?: string
  name: string
  asset_type?: string
  lifecycle_state?: LifecycleState
  manufacturer?: string
  capacity?: string
  commissioned_date?: string
  location?: string
  pos_x?: number
  pos_y?: number
  floor?: number
  rotation?: number
  width?: number
  depth?: number
  height?: number
}
export type AssetUpdate = Partial<Omit<AssetCreate, 'asset_id' | 'lifecycle_state'>>
export type AssetEvent = {
  event_id: string
  from_state?: string | null
  to_state: string
  note?: string | null
  created_by?: string | null
  created_at?: string | null
}

export type Calibration = {
  origin_x: number; origin_y: number; scale: number; rotation: number
  plan_w?: number; plan_h?: number
}
export type Schematic = {
  schematic_id: string
  site_id: string
  file_name?: string | null
  file_type?: string | null
  kind?: string | null
  calibration?: Calibration | null
  uploaded_at?: string | null
}

const qk = {
  assets: (s: string) => ['assets', s] as const,
  asset: (a: string) => ['asset', a] as const,
  events: (a: string) => ['asset', a, 'events'] as const,
  schematics: (s: string) => ['schematics', s] as const,
}

// ---- register reads ----
export const useSiteAssets = (siteId: string) =>
  useQuery({ queryKey: qk.assets(siteId), queryFn: () => getJson<Asset[]>(`/sites/${siteId}/assets`), enabled: !!siteId })

export const useAsset = (assetId?: string) =>
  useQuery({ queryKey: qk.asset(assetId ?? ''), queryFn: () => getJson<Asset>(`/assets/${assetId}`), enabled: !!assetId })

export const useAssetEvents = (assetId?: string) =>
  useQuery({ queryKey: qk.events(assetId ?? ''), queryFn: () => getJson<AssetEvent[]>(`/assets/${assetId}/events`), enabled: !!assetId })

// ---- register mutations ----
function invalidateAsset(qc: ReturnType<typeof useQueryClient>, siteId: string, assetId?: string) {
  qc.invalidateQueries({ queryKey: qk.assets(siteId) })
  if (assetId) {
    qc.invalidateQueries({ queryKey: qk.asset(assetId) })
    qc.invalidateQueries({ queryKey: qk.events(assetId) })
  }
  qc.invalidateQueries({ queryKey: ['ops'] }) // register change may add/remove a streaming asset
}

export function useCreateAsset(siteId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: AssetCreate) => api.post(`/sites/${siteId}/assets`, body).then((r) => r.data as Asset),
    onSuccess: () => invalidateAsset(qc, siteId),
  })
}

export function useUpdateAsset(siteId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ assetId, patch }: { assetId: string; patch: AssetUpdate }) =>
      api.put(`/assets/${assetId}`, patch).then((r) => r.data as Asset),
    onSuccess: (_d, v) => invalidateAsset(qc, siteId, v.assetId),
  })
}

export function useTransitionAsset(siteId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ assetId, to_state, note }: { assetId: string; to_state: LifecycleState; note?: string }) =>
      api.post(`/assets/${assetId}/transition`, { to_state, note }).then((r) => r.data as Asset),
    onSuccess: (_d, v) => invalidateAsset(qc, siteId, v.assetId),
  })
}

export function useDeleteAsset(siteId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (assetId: string) => api.delete(`/assets/${assetId}`).then((r) => r.data),
    onSuccess: () => invalidateAsset(qc, siteId),
  })
}

// ---- schematics ----
export const useSchematics = (siteId: string) =>
  useQuery({ queryKey: qk.schematics(siteId), queryFn: () => getJson<Schematic[]>(`/sites/${siteId}/schematics`), enabled: !!siteId })

export function useUploadSchematic(siteId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData()
      fd.append('file', file)
      return api.post(`/sites/${siteId}/schematics`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
        .then((r) => r.data as { schematic_id: string })
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.schematics(siteId) }),
  })
}

export function useSetCalibration(siteId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ schematicId, calibration }: { schematicId: string; calibration: Calibration }) =>
      api.put(`/schematics/${schematicId}/calibration`, calibration).then((r) => r.data as Schematic),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.schematics(siteId) }),
  })
}

export function useDeleteSchematic(siteId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (schematicId: string) => api.delete(`/schematics/${schematicId}`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.schematics(siteId) }),
  })
}

/**
 * Fetch a schematic's raw bytes as an object URL (the /raw route is bearer-gated,
 * so an <img src> can't hit it directly — we fetch with auth and wrap in a blob URL).
 * Used as the twin's underlay texture.
 */
export function useSchematicImage(schematicId?: string): string | undefined {
  const [url, setUrl] = useState<string>()
  useEffect(() => {
    if (!schematicId) return
    let revoked: string | undefined
    let alive = true
    api.get(`/schematics/${schematicId}/raw`, { responseType: 'blob' }).then((r) => {
      if (!alive) return
      revoked = URL.createObjectURL(r.data as Blob)
      setUrl(revoked)
    }).catch(() => {})
    return () => { alive = false; if (revoked) URL.revokeObjectURL(revoked) }
  }, [schematicId])
  return url
}

export const schematicRawUrl = (schematicId: string) => `${BASE}/schematics/${schematicId}/raw`
